// src/components/sensors/ViewSensorDetails.js
// --- UPDATE: V2 — production hardening + UI
//   • Stale-while-revalidate details cache (instant reopen) + race-safe fetching
//   • Online/offline TTL re-evaluated every 15s (was frozen at first render)
//   • Errors no longer hide live telemetry — inline banner with Retry
//   • No fabricated values: missing signal / battery / pressure show "Not reported"
//     instead of 95% / 100% / 0.0 PSI
//   • Live session trend (sparkline + delta) built from incoming liveReading updates
//   • PSI gauge (same 20 / 100 PSI thresholds as ReadingTable) and water-level tank visual
//   • AI card: confidence, server/edge source, trend, top drivers, recommendations, freshness
//   • Diagnostics: signal / battery thresholds, data freshness, firmware / model / install
//   • Topology: copy coordinates, open in Maps, locate on map, inspect binding
//   • Raw payload viewer (collapsible, copyable) for engineers
//   • Skeleton loading, sticky action footer, reduced-motion aware
//   All new props are optional: onViewHistory, onLocate, cacheTtlMs.
// --- END UPDATE
// --- UPDATE: V3 — fully connected sensor details
//   • Loads everything FlowSense has on the sensor in one call (GET /sensors/:id/context):
//     enriched record (latest reading, last activity, AI), linked asset, newest 120 readings,
//     open alerts and open work orders on the linked asset. Falls back to GET /sensors/:id on
//     older backends, and says so when the device isn't in the register.
//   • Own live feed: subscribes to this device's WebSocket readings while open (case-insensitive),
//     so it no longer depends on the page's exact-case `liveReading` lookup.
//   • Same live model as the tables: Transmitting / Online / Delayed / Offline / No data, from when
//     data was last heard — not from a 5-minute TTL on the device timestamp.
//   • Recent readings chart + stats (min / avg / max / σ or time per level, cadence, gaps).
//   • FlowSense AI: flood forecast (page model or sensor record), linked-asset health / RUL, edge
//     anomaly check on the real readings, and ranked insights (offline, cadence, limits, alerts,
//     work orders, missing links) — every one computed from real data.
//   • Alerts & work orders, linked asset card, full registry specs.
//   • "View history" works without the page wiring it (opens the history modal itself).
//   • Performance: the body (fetch, feed, clock) only exists while the drawer is open; sections
//     are memoised; shared 5 s clock; no infinite animations; raw JSON stringified only when shown.
//   New optional props: onOpenWorkOrder(id), liveThresholds.
// --- END UPDATE
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, Divider, Drawer, DrawerBody, DrawerCloseButton,
  DrawerContent, DrawerFooter, DrawerHeader, DrawerOverlay, HStack, Icon, IconButton, SimpleGrid, Skeleton,
  SkeletonText, Spinner, Text, Tooltip, VStack, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { MdContentCopy, MdHistory, MdLink, MdMyLocation, MdRefresh, MdSensors, MdWaterDrop } from 'react-icons/md';

import { useNotifications } from '../../contexts/NotificationContext';
import { detailsKey, fetchSensorDetailsBundle, peekCached } from '../../services/sensorInsightsService';
import { useNow } from './useLiveSensorRegistry';
import { useSensorLiveFeed } from './useSensorLiveFeed';
import {
  LIVE_STATE_META, formatAgo, mergeThresholds, readingTimeMs, resolveLiveState, sensorActivityMs, toMillis,
} from './sensorLiveStatus';
import { deriveSensorAi } from './sensorAiInsights';
import { buildSensorInsights, mergeReadings, readingStats } from './sensorDetailsInsights';
import {
  AlertsAndWorkOrders, DiagnosticsCard, FlowSenseAiCard, LinkedAssetCard, LiveHero, RawPayload,
  RecentReadingsCard, SectionTitle, TopologyCard,
} from './SensorDetailsSections';
import ViewHistory from './ViewHistory';

const CLOCK_MS = 5000;
const DEFAULT_CACHE_TTL_MS = 30000;

const hasValue = (v) => v !== null && v !== undefined && v !== '';
const toNum = (v) => (hasValue(v) && Number.isFinite(Number(v)) ? Number(v) : null);

// Mirrors sensorIntelService.isWaterSensor on the backend
const isWaterRecord = (s) => ['water_level', 'well', 'float', 'level'].includes(String(s?.type || '').toLowerCase())
  || String(s?.linked_entity_type || '').toLowerCase() === 'well';

/** The record's latest_reading ({kind, value, timestamp}) as a normal reading object. */
const fromLatestReading = (lr) => {
  if (!lr || !lr.timestamp) return null;
  return lr.kind === 'water_level' ? { level: lr.level ?? lr.value, timestamp: lr.timestamp } : { psi: lr.value, timestamp: lr.timestamp };
};

const normaliseFactors = (raw) => {
  if (!raw) return [];
  const list = Array.isArray(raw)
    ? raw.map((f) => (typeof f === 'string' ? { label: f, weight: null } : { label: f?.label ?? f?.feature ?? f?.name ?? 'Factor', weight: toNum(f?.weight ?? f?.importance ?? f?.impact) }))
    : typeof raw === 'object' ? Object.entries(raw).map(([label, weight]) => ({ label, weight: toNum(weight) })) : [];
  return list
    .map((f) => ({ ...f, label: String(f.label).replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) }))
    .sort((a, b) => Math.abs(b.weight ?? 0) - Math.abs(a.weight ?? 0))
    .slice(0, 3);
};
const normaliseRecs = (raw) => (!raw ? [] : (Array.isArray(raw) ? raw : [raw])
  .map((r) => (typeof r === 'string' ? r : r?.action ?? r?.text ?? '')).filter(Boolean).slice(0, 3));

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(String(text)); return true; } catch { return false; }
};

/** Mounted only while the drawer is open: owns the fetch, the live feed and the clock. */
function SensorDetailsBody({
  sensor, liveReading, aiPredictions, onSelectEntity, onViewHistory, onLocate, onOpenWorkOrder,
  cacheTtlMs, liveThresholds,
}) {
  const deviceId = sensor.deviceId || sensor.device_id || sensor.id;
  const thresholds = useMemo(() => mergeThresholds(liveThresholds), [liveThresholds]);
  const now = useNow(CLOCK_MS);
  const { isConnected } = useNotifications();
  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtextColor = useColorModeValue('gray.500', 'gray.400');
  const footerBg = useColorModeValue('whiteAlpha.900', 'blackAlpha.500');

  // ── registry bundle (stale-while-revalidate, race-safe) ──────────────────
  const [bundle, setBundle] = useState(() => peekCached(detailsKey(deviceId)));
  const [load, setLoad] = useState(() => ({ loading: !peekCached(detailsKey(deviceId)), refreshing: false, error: null }));
  const reqRef = useRef(0);
  const fetchBundle = useCallback(async (force = false) => {
    const id = ++reqRef.current;
    const hadData = !!peekCached(detailsKey(deviceId));
    setLoad({ loading: !hadData, refreshing: hadData, error: null });
    try {
      const data = await fetchSensorDetailsBundle(deviceId, { force, ttlMs: cacheTtlMs });
      if (id !== reqRef.current) return;
      setBundle(data);
      setLoad({ loading: false, refreshing: false, error: null });
    } catch (e) {
      if (id !== reqRef.current) return;
      setLoad({ loading: false, refreshing: false, error: 'Unable to load this sensor from the FlowSense registry.' });
    }
  }, [deviceId, cacheTtlMs]);
  useEffect(() => {
    setBundle(peekCached(detailsKey(deviceId)));
    fetchBundle(false);
    return () => { reqRef.current += 1; }; // ignore responses after close / sensor switch
  }, [deviceId, fetchBundle]);

  // ── live feed (this device only) ─────────────────────────────────────────
  const feed = useSensorLiveFeed(deviceId, { enabled: true });

  // ── merged record + kind ────────────────────────────────────────────────
  const details = bundle?.sensor || null;
  const record = useMemo(() => ({ ...sensor, ...(details || {}), device_id: details?.device_id || deviceId }), [sensor, details, deviceId]);
  const kind = bundle?.kind || (isWaterRecord(record) ? 'water_level' : 'pressure');
  const isWater = kind === 'water_level';
  const registered = bundle ? bundle.registered !== false : true;

  // ── newest reading from every source ────────────────────────────────────
  const latest = useMemo(() => {
    const ctxNewest = bundle?.readings?.[0]
      ? (isWater ? { level: bundle.readings[0].value, timestamp: bundle.readings[0].timestamp } : { psi: bundle.readings[0].value, timestamp: bundle.readings[0].timestamp })
      : null;
    const candidates = [feed.latest, liveReading, fromLatestReading(details?.latest_reading), ctxNewest].filter(Boolean);
    let best = null; let bestMs = -Infinity;
    candidates.forEach((r) => { const t = readingTimeMs(r) ?? -1; if (t > bestMs) { best = r; bestMs = t; } });
    return best;
  }, [feed.latest, liveReading, details, bundle, isWater]);

  const measuredMs = latest ? toMillis(latest.timestamp) ?? readingTimeMs(latest) : null;
  const rawPsi = !isWater ? toNum(latest?.psi ?? latest?.pressure ?? latest?.value) : null;
  const rawLevel = isWater && hasValue(latest?.level ?? latest?.water_level) ? String(latest.level ?? latest.water_level).toUpperCase() : null;

  // ── live state (same model as the tables) ───────────────────────────────
  const creditedArrival = feed.lastArrivalMs && (feed.lastArrivalMeasuredMs === null || feed.lastArrivalMs - feed.lastArrivalMeasuredMs <= thresholds.onlineMs)
    ? feed.lastArrivalMs : null;
  const clockSkew = measuredMs !== null && measuredMs > now + thresholds.futureSkewMs;
  const heard = [creditedArrival, measuredMs !== null ? Math.min(measuredMs, now) : null, sensorActivityMs(record)].filter((v) => v !== null && Number.isFinite(v));
  const lastHeardMs = heard.length ? Math.min(Math.max(...heard), now) : null;
  const liveState = resolveLiveState(lastHeardMs, now, thresholds);
  const ageLabel = formatAgo(lastHeardMs, now);
  const row = useMemo(() => ({ liveState, lastHeardMs, measuredMs, arrivals: feed.arrivals, clockSkew }), [liveState, lastHeardMs, measuredMs, feed.arrivals, clockSkew]);

  // ── recent readings + stats ─────────────────────────────────────────────
  const points = useMemo(
    () => mergeReadings({ kind, contextReadings: bundle?.readings || [], liveRecent: feed.recent, extra: liveReading ? [liveReading] : [] }),
    [kind, bundle, feed.recent, liveReading]
  );
  const stats = useMemo(() => readingStats(points, kind), [points, kind]);
  const delta = !isWater && stats.prev !== null && stats.prev !== undefined && stats.last !== null ? stats.last - stats.prev : null;

  // ── FlowSense AI ────────────────────────────────────────────────────────
  const assetId = record.asset_id || (record.linked_entity_type && !['pipeline', 'none'].includes(record.linked_entity_type) ? record.linked_entity_id : null);
  const pagePrediction = (aiPredictions && ((assetId !== null && assetId !== undefined && aiPredictions[String(assetId)]) || aiPredictions[String(deviceId)])) || null;
  const ai = useMemo(() => deriveSensorAi({
    kind,
    sensor: record,
    prediction: pagePrediction,
    psiValues: isWater ? [] : points.map((p) => p.value),
    levelHistory: isWater ? points.slice().reverse().map((p) => ({ level: p.level, timestamp: new Date(p.t).toISOString() })) : [],
  }), [kind, record, pagePrediction, points, isWater]);
  const aiMeta = useMemo(() => {
    const src = pagePrediction || record.ai_predictions || {};
    const conf = toNum(src.confidence ?? src.model_confidence ?? src.flood_confidence);
    return {
      source: pagePrediction ? (pagePrediction.source === 'edge' ? 'edge' : 'server') : record.ai_predictions ? 'server' : null,
      stale: !!pagePrediction?.serverStale,
      confidence: conf === null ? null : Math.round(conf > 1 ? conf : conf * 100),
      trend: src.trend || null,
      updatedAt: src.updated_at ?? src.receivedAt ?? src.generated_at ?? null,
      modelVersion: src.model_version ?? null,
      forecast: Array.isArray(src.probability_forecast) ? src.probability_forecast.map(Number).filter(Number.isFinite) : [],
      factors: normaliseFactors(src.top_factors ?? src.contributing_factors ?? src.feature_importance),
      recommendations: normaliseRecs(src.recommendations ?? src.recommended_action),
    };
  }, [pagePrediction, record]);

  const alerts = useMemo(() => bundle?.active_alerts || [], [bundle]);
  const workOrders = useMemo(() => bundle?.open_work_orders || [], [bundle]);
  const insights = useMemo(() => buildSensorInsights({
    kind, liveState, lastHeardMs, measuredMs, clockSkew, now, thresholds, stats, ai, alerts, workOrders,
    sensor: record, asset: bundle?.asset || null, registered, feedConnected: isConnected,
  // ageLabel stands in for `now` so insights recompute when the age text changes, not every tick
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [kind, liveState, ageLabel, measuredMs, clockSkew, thresholds, stats, ai, alerts, workOrders, record, bundle, registered, isConnected]);

  // ── handlers ────────────────────────────────────────────────────────────
  const [historyOpen, setHistoryOpen] = useState(false);
  const handleCopy = useCallback(async (text, label) => {
    const ok = await copyText(text);
    toast({ title: ok ? `${label} copied` : 'Copy failed', description: ok ? String(text).slice(0, 80) : 'Clipboard is unavailable in this browser.', status: ok ? 'success' : 'warning', duration: 1800, position: 'bottom-right' });
  }, [toast]);
  const openHistory = useCallback(() => {
    if (onViewHistory) onViewHistory(deviceId);
    else setHistoryOpen(true);
  }, [onViewHistory, deviceId]);
  const closeHistory = useCallback(() => setHistoryOpen(false), []);
  const historySensor = useMemo(() => ({ ...record, type: isWater ? 'water_level' : record.type || 'pressure' }), [record, isWater]);
  const lat = parseFloat(record.latitude);
  const lng = parseFloat(record.longitude);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const locate = useMemo(() => (onLocate && hasCoords ? () => onLocate(record, { lat, lng }) : null), [onLocate, hasCoords, record, lat, lng]);
  const linkedType = record.linked_entity_type && record.linked_entity_type !== 'none' ? record.linked_entity_type : assetId ? 'asset' : null;
  const linkedId = record.linked_entity_type && record.linked_entity_type !== 'none' ? record.linked_entity_id : assetId;

  const signalQuality = toNum(latest?.quality ?? details?.quality);
  const batteryLevel = toNum(details?.battery_level ?? latest?.battery_level ?? latest?.battery);
  const specs = useMemo(() => {
    const d = (v) => (v ? new Date(v).toLocaleString() : null);
    return [
      { label: 'Status', value: record.status },
      { label: 'Location', value: record.location_description },
      { label: 'Unit', value: record.unit || (isWater ? 'level' : 'PSI') },
      { label: 'Sensor type', value: record.sensor_type },
      { label: 'Data type', value: record.data_type },
      { label: 'Pipeline', value: record.pipeline_id ? `#${record.pipeline_id}${record.linked_entity_name ? ` · ${record.linked_entity_name}` : ''}` : null },
      { label: 'Model', value: record.model || record.model_number },
      { label: 'Firmware', value: record.firmware_version || record.firmware },
      { label: 'Registered', value: d(record.created_at) },
      { label: 'Last seen (server)', value: d(record.last_seen_at) },
      { label: 'Last activity', value: d(record.last_activity_at) },
      { label: 'Record updated', value: d(record.updated_at) },
    ].filter((s) => hasValue(s.value));
  }, [record, isWater]);
  const offlineNote = liveState === 'offline' && lastHeardMs
    ? `No transmission for ${ageLabel?.replace(' ago', '') || 'over 5 minutes'}. Check power, Wi-Fi coverage and the node's display / serial log.`
    : liveState === 'no_data' ? 'This node has not reported yet. Check power, Wi-Fi and the server address configured on it.' : null;
  const rawPayload = useMemo(() => ({ sensor, liveReading, latest, bundle, feed: { arrivals: feed.arrivals, lastArrivalMs: feed.lastArrivalMs } }), [sensor, liveReading, latest, bundle, feed.arrivals, feed.lastArrivalMs]);
  const meta = LIVE_STATE_META[liveState];
  const showSkeleton = load.loading && !latest && !bundle;

  return (
    <>
      <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
        <HStack spacing={3}>
          <Box p={2} bg={isWater ? 'cyan.500' : 'blue.500'} borderRadius="xl" color="white" boxShadow="sm">
            <Icon as={isWater ? MdWaterDrop : MdSensors} boxSize={6} />
          </Box>
          <VStack align="start" spacing={0} minW={0}>
            <Text fontWeight="800" fontSize="lg">Sensor Intelligence</Text>
            <HStack spacing={1}>
              <Text fontSize="xs" color={subtextColor} fontFamily="mono" data-testid="sensor-device-id">{deviceId}</Text>
              <IconButton size="xs" h="18px" minW="18px" variant="ghost" icon={<MdContentCopy />} onClick={() => handleCopy(deviceId, 'Device ID')} aria-label="Copy device ID" />
            </HStack>
            {record.name && <Text fontSize="xs" fontWeight="600" noOfLines={1}>{record.name}</Text>}
          </VStack>
          <HStack ml="auto" mr={8} spacing={2}>
            <Badge colorScheme={meta.color} variant="subtle" borderRadius="full" px={2} data-testid="sensor-live-badge">{meta.short}</Badge>
            <Tooltip label="Refresh from the registry" hasArrow>
              <IconButton size="sm" variant="ghost" icon={<MdRefresh />} isLoading={load.loading || load.refreshing} onClick={() => fetchBundle(true)} aria-label="Refresh sensor details" />
            </Tooltip>
          </HStack>
        </HStack>
      </DrawerHeader>

      <DrawerBody py={5}>
        {showSkeleton ? (
          <VStack spacing={5} align="stretch" aria-busy="true">
            <Skeleton h="110px" borderRadius="2xl" />
            <Skeleton h="150px" borderRadius="2xl" />
            <SimpleGrid columns={2} spacing={3}><Skeleton h="80px" borderRadius="xl" /><Skeleton h="80px" borderRadius="xl" /></SimpleGrid>
            <SkeletonText noOfLines={4} spacing={3} skeletonHeight="3" />
            <HStack justify="center" color={subtextColor} fontSize="sm" fontWeight="600"><Spinner size="xs" /><Text>Loading sensor…</Text></HStack>
          </VStack>
        ) : (
          <VStack spacing={5} align="stretch">
            {load.error && (
              <Alert status="error" borderRadius="xl" variant="left-accent">
                <AlertIcon />
                <AlertDescription fontSize="sm" flex="1">
                  {load.error}{bundle ? ' Showing the last data loaded.' : latest ? ' Live readings are still shown.' : ''}
                </AlertDescription>
                <Button size="xs" leftIcon={<MdRefresh />} onClick={() => fetchBundle(true)} ml={2}>Retry</Button>
              </Alert>
            )}
            {bundle && !registered && (
              <Alert status="warning" borderRadius="xl" variant="left-accent" fontSize="sm">
                <AlertIcon />This device sends readings but has no sensor record. Register it to link it, place it and enable alerts.
              </Alert>
            )}

            <LiveHero kind={kind} rawPsi={rawPsi} rawLevel={rawLevel} row={row} thresholds={thresholds} ageLabel={ageLabel} measuredMs={measuredMs} delta={delta} />
            <FlowSenseAiCard kind={kind} ai={ai} meta={aiMeta} insights={insights} now={now} />
            <RecentReadingsCard kind={kind} points={points} stats={stats} onViewHistory={openHistory} isLoading={load.loading} />
            <AlertsAndWorkOrders alerts={alerts} workOrders={workOrders} assetName={bundle?.asset?.name} onOpenWorkOrder={onOpenWorkOrder} now={now} isLoading={load.loading} />
            <Box>
              <SectionTitle>Topology & binding</SectionTitle>
              <VStack align="stretch" spacing={2.5}>
                <TopologyCard kind={kind} typeLabel={record.type || kind} lat={lat} lng={lng} onCopy={handleCopy} onLocate={locate} />
                {linkedId ? (
                  <LinkedAssetCard asset={bundle?.asset || null} linkedType={linkedType} linkedId={linkedId} onSelectEntity={onSelectEntity} />
                ) : (
                  <Text fontSize="xs" color={subtextColor} px={1}>Not linked to an asset or pipeline.</Text>
                )}
              </VStack>
            </Box>
            <DiagnosticsCard signalQuality={signalQuality} batteryLevel={batteryLevel} specs={specs} offlineNote={offlineNote} />
            <Divider borderColor={borderColor} />
            <RawPayload payload={rawPayload} onCopy={handleCopy} />
          </VStack>
        )}
      </DrawerBody>

      <DrawerFooter borderTopWidth="1px" borderColor={borderColor} bg={footerBg} backdropFilter="blur(8px)" gap={2} flexWrap="wrap">
        {locate && <Button size="sm" variant="outline" leftIcon={<MdMyLocation />} onClick={locate}>Locate</Button>}
        {linkedId && onSelectEntity && (
          <Button size="sm" variant="outline" colorScheme="purple" leftIcon={<MdLink />} onClick={() => onSelectEntity(linkedType, bundle?.asset || { id: linkedId })}>
            Inspect {String(linkedType).toLowerCase()}
          </Button>
        )}
        <Button size="sm" colorScheme={isWater ? 'cyan' : 'blue'} leftIcon={<MdHistory />} onClick={openHistory}>View history</Button>
      </DrawerFooter>

      {/* Opened here only when the page didn't pass onViewHistory */}
      {!onViewHistory && <ViewHistory isOpen={historyOpen} onClose={closeHistory} sensor={historySensor} />}
    </>
  );
}

function ViewSensorDetails({
  isOpen,
  onClose,
  sensor,
  liveReading,
  aiPredictions = {},
  onSelectEntity,
  // --- UPDATE: optional props
  onViewHistory,
  onLocate,
  cacheTtlMs = DEFAULT_CACHE_TTL_MS,
  // --- END UPDATE
  // --- UPDATE: V3 optional props
  onOpenWorkOrder,
  liveThresholds = null,
  // --- END UPDATE
}) {
  const bgColor = useColorModeValue('white', 'gray.900');
  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay backdropFilter="blur(4px)" />
      <DrawerContent bg={bgColor} borderLeftRadius="2xl">
        <DrawerCloseButton />
        {/* --- UPDATE: V3 — the body (fetch, live feed, clock) exists only while open */}
        {isOpen && sensor ? (
          <SensorDetailsBody
            key={String(sensor.deviceId || sensor.device_id || sensor.id)}
            sensor={sensor}
            liveReading={liveReading}
            aiPredictions={aiPredictions}
            onSelectEntity={onSelectEntity}
            onViewHistory={onViewHistory}
            onLocate={onLocate}
            onOpenWorkOrder={onOpenWorkOrder}
            cacheTtlMs={cacheTtlMs}
            liveThresholds={liveThresholds}
          />
        ) : isOpen ? (
          <DrawerBody py={10}><Text textAlign="center" color="gray.500">No sensor selected.</Text></DrawerBody>
        ) : null}
        {/* --- END UPDATE */}
      </DrawerContent>
    </Drawer>
  );
}

export default memo(ViewSensorDetails);
